import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { getPiezasPendientesParaSelector, loadPlanTrabajoContext } from '../../../services/planTrabajoService';
import { getCurrentEmpleado } from '../../../services/planTrabajoService';
import { useAuth } from '../../../contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const ESTADO_CONFIG = {
  PENDIENTE:  { label: 'Pendiente',  cls: 'bg-white/10 text-white/50',          dot: 'bg-white/40' },
  EN_PROCESO: { label: 'En proceso', cls: 'bg-yellow-400/15 text-yellow-400',   dot: 'bg-yellow-400' },
  INCIDENCIA: { label: 'Incidencia', cls: 'bg-red-400/15 text-red-400',         dot: 'bg-red-400' },
  COMPLETADA: { label: 'Completada', cls: 'bg-green-400/15 text-green-400',     dot: 'bg-green-400' },
};

const FILTROS = [
  { id: 'hoy',       label: 'Hoy' },
  { id: 'semana',    label: 'Esta semana' },
  { id: 'proximos',  label: 'Próximos' },
  { id: 'mis',       label: 'Mis piezas' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str?.split('-')?.map(Number);
  return new Date(y, m - 1, d);
};

const getToday = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};

const toDateStr = (d) => {
  const y = d?.getFullYear();
  const m = String(d?.getMonth() + 1)?.padStart(2, '0');
  const dd = String(d?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const formatDayHeader = (dateStr) => {
  if (!dateStr) return 'Sin fecha';
  const today = toDateStr(getToday());
  const tomorrow = toDateStr(new Date(getToday().getTime() + 86400000));
  if (dateStr === today) return 'HOY';
  if (dateStr === tomorrow) return 'MAÑANA';
  const d = toLocalDate(dateStr);
  return d?.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })?.toUpperCase();
};

const getWeekEnd = () => {
  const today = getToday();
  const dow = today?.getDay() === 0 ? 7 : today?.getDay();
  const daysToSat = 6 - dow;
  return new Date(today.getTime() + daysToSat * 86400000);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ColorDot = ({ color, size = 10 }) => (
  <span
    className="inline-block rounded-full flex-shrink-0"
    style={{
      width: size,
      height: size,
      backgroundColor: color || '#D6EF4E',
      boxShadow: `0 0 6px ${color || '#D6EF4E'}60`,
    }}
  />
);

const EstadoBadge = ({ estado }) => {
  const cfg = ESTADO_CONFIG?.[estado] || ESTADO_CONFIG?.PENDIENTE;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-oranienbaum px-2 py-0.5 rounded-full ${cfg?.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
      {cfg?.label}
    </span>
  );
};

// Tarjeta de trabajo con sus piezas agrupadas
const TrabajoCard = ({ trabajo, piezas, clienteColor, onSelectPieza, loadingPiezaId }) => {
  const clienteNombre = trabajo?.cliente?.nombre || '—';
  const productoNombre = trabajo?.producto?.nombre || '—';
  const empleadoNombre = trabajo?.empleado?.nombre || null;
  const color = clienteColor || '#D6EF4E';

  return (
    <div
      className="rounded-xl border border-white/8 bg-[#111111] overflow-hidden"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      {/* Header del trabajo */}
      <div className="px-4 py-3 border-b border-white/5">
        <div className="flex items-start gap-2">
          <ColorDot color={color} size={8} />
          <div className="flex-1 min-w-0">
            <p className="font-bold font-oranienbaum text-white text-sm leading-tight truncate">
              {clienteNombre}
            </p>
            <p className="text-xs font-oranienbaum text-white/60 mt-0.5 truncate">{productoNombre}</p>
            {empleadoNombre && (
              <p className="text-[11px] font-oranienbaum text-white/40 mt-0.5 flex items-center gap-1">
                <Icon name="User" size={10} className="flex-shrink-0" />
                {empleadoNombre}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Piezas */}
      <div className="divide-y divide-white/5">
        {piezas?.map((pieza) => {
          const isLoading = loadingPiezaId === pieza?.id;
          const isCompletada = pieza?.estado === 'COMPLETADA';
          const isEnProceso = pieza?.estado === 'EN_PROCESO';
          const btnLabel = isCompletada ? 'Ver registro' : isEnProceso ? 'Continuar' : 'Seleccionar';
          const btnIcon = isCompletada ? 'Eye' : isEnProceso ? 'Play' : 'ChevronRight';

          return (
            <div
              key={pieza?.id}
              className={`flex items-center gap-3 px-4 py-3 ${isCompletada ? 'opacity-60' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-oranienbaum text-white/70">
                    Pieza {pieza?.numero_pieza} de {trabajo?.cantidad_piezas || 1}
                  </span>
                  <EstadoBadge estado={pieza?.estado} />
                </div>
                {pieza?.empleado?.nombre && pieza?.empleado?.nombre !== empleadoNombre && (
                  <p className="text-[10px] font-oranienbaum text-white/35 mt-0.5 flex items-center gap-1">
                    <Icon name="User" size={9} />
                    {pieza?.empleado?.nombre}
                  </p>
                )}
              </div>
              <button
                onClick={() => onSelectPieza(pieza)}
                disabled={isLoading}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold
                  transition-all active:scale-95 flex-shrink-0 min-w-[100px] justify-center
                  ${isCompletada
                    ? 'bg-green-400/10 text-green-400 border border-green-400/20 hover:bg-green-400/20'
                    : isEnProceso
                    ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20 hover:bg-yellow-400/20' :'bg-[#D6EF4E]/10 text-[#D6EF4E] border border-[#D6EF4E]/25 hover:bg-[#D6EF4E]/20'
                  }
                  disabled:opacity-50
                `}
              >
                {isLoading
                  ? <Icon name="Loader2" size={12} className="animate-spin" />
                  : <Icon name={btnIcon} size={12} />
                }
                {btnLabel}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Modal Component ─────────────────────────────────────────────────────

const PlanTrabajoSelectorModal = ({ isOpen, onClose, onSelectPieza, empleadoActualId, isAdmin }) => {
  const [allPiezas, setAllPiezas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPiezaId, setLoadingPiezaId] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filtroActivo, setFiltroActivo] = useState(isAdmin ? 'hoy' : 'mis');
  const [incluirCompletadas, setIncluirCompletadas] = useState(false);
  const searchRef = useRef(null);

  const loadPiezas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPiezasPendientesParaSelector({
        incluirCompletadas,
        empleadoId: empleadoActualId,
        isAdmin,
      });
      setAllPiezas(data || []);
    } catch (err) {
      console.error('[PlanTrabajoSelectorModal] Error:', err);
      setError('Error al cargar piezas del Plan de Trabajo');
    } finally {
      setLoading(false);
    }
  }, [incluirCompletadas, empleadoActualId, isAdmin]);

  useEffect(() => {
    if (isOpen) {
      loadPiezas();
      setTimeout(() => searchRef?.current?.focus(), 100);
    }
  }, [isOpen, loadPiezas]);

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredPiezas = React.useMemo(() => {
    let piezas = [...allPiezas];
    const today = toDateStr(getToday());
    const weekEnd = toDateStr(getWeekEnd());

    // Filtro temporal
    if (filtroActivo === 'hoy') {
      piezas = piezas?.filter(p => p?.trabajo?.fecha === today);
    } else if (filtroActivo === 'semana') {
      piezas = piezas?.filter(p => {
        const f = p?.trabajo?.fecha;
        return f && f >= today && f <= weekEnd;
      });
    } else if (filtroActivo === 'proximos') {
      piezas = piezas?.filter(p => {
        const f = p?.trabajo?.fecha;
        return f && f > weekEnd;
      });
    } else if (filtroActivo === 'mis') {
      piezas = piezas?.filter(p => {
        const empPieza = p?.empleado_id;
        const empTrabajo = p?.trabajo?.empleado_id;
        return empPieza === empleadoActualId || (!empPieza && empTrabajo === empleadoActualId);
      });
    }

    // Búsqueda
    if (search?.trim()) {
      const q = search?.trim()?.toLowerCase();
      piezas = piezas?.filter(p => {
        const cliente = p?.trabajo?.cliente?.nombre?.toLowerCase() || '';
        const producto = (p?.producto?.nombre || p?.trabajo?.producto?.nombre || '')?.toLowerCase();
        const empleado = (p?.empleado?.nombre || p?.trabajo?.empleado?.nombre || '')?.toLowerCase();
        const numPieza = String(p?.numero_pieza || '');
        return cliente?.includes(q) || producto?.includes(q) || empleado?.includes(q) || numPieza?.includes(q);
      });
    }

    return piezas;
  }, [allPiezas, filtroActivo, search, empleadoActualId]);

  // ── Group by date, then by trabajo, respecting orden_dia ──────────────────
  const grouped = React.useMemo(() => {
    // Group piezas by fecha
    const byFecha = {};
    for (const pieza of filteredPiezas) {
      const fecha = pieza?.trabajo?.fecha || '__sin_fecha__';
      if (!byFecha?.[fecha]) byFecha[fecha] = [];
      byFecha?.[fecha]?.push(pieza);
    }

    // Sort dates: today first, then ascending
    const today = toDateStr(getToday());
    const sortedFechas = Object.keys(byFecha)?.sort((a, b) => {
      if (a === '__sin_fecha__') return 1;
      if (b === '__sin_fecha__') return -1;
      if (a === today) return -1;
      if (b === today) return 1;
      return a?.localeCompare(b);
    });

    return sortedFechas?.map(fecha => {
      const piezasDelDia = byFecha?.[fecha];

      // Group by trabajo_id, respecting orden_dia
      const trabajoMap = {};
      for (const pieza of piezasDelDia) {
        const tid = pieza?.trabajo?.id;
        if (!trabajoMap?.[tid]) {
          trabajoMap[tid] = {
            trabajo: pieza?.trabajo,
            piezas: [],
            orden_dia: pieza?.trabajo?.orden_dia ?? 9999,
          };
        }
        trabajoMap?.[tid]?.piezas?.push(pieza);
      }

      // Sort trabajos by orden_dia ASC, then by numero_pieza within each
      const trabajos = Object.values(trabajoMap)?.sort((a, b) => a?.orden_dia - b?.orden_dia);
      for (const t of trabajos) {
        t?.piezas?.sort((a, b) => (a?.numero_pieza || 0) - (b?.numero_pieza || 0));
      }

      return { fecha, trabajos };
    });
  }, [filteredPiezas]);

  const handleSelectPieza = async (pieza) => {
    setLoadingPiezaId(pieza?.id);
    try {
      const ctx = await loadPlanTrabajoContext(pieza?.id);
      onSelectPieza(ctx, pieza);
      onClose();
    } catch (err) {
      console.error('[PlanTrabajoSelectorModal] Error loading context:', err);
      setError(err?.message || 'Error al cargar datos de la pieza');
    } finally {
      setLoadingPiezaId(null);
    }
  };

  if (!isOpen) return null;

  const totalVisible = filteredPiezas?.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-white/10 bg-[#0D0D0D] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#D6EF4E]/10 flex items-center justify-center">
              <Icon name="ClipboardList" size={16} className="text-[#D6EF4E]" />
            </div>
            <div>
              <h2 className="font-bold font-oranienbaum text-white text-base leading-tight">
                Trabajo Planificado
              </h2>
              <p className="text-[11px] font-oranienbaum text-white/40">
                {loading ? 'Cargando...' : `${totalVisible} pieza${totalVisible !== 1 ? 's' : ''} disponible${totalVisible !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white/70 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3 flex-shrink-0">
          <div className="relative">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Buscar cliente, producto, empleado..."
              className="w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-xl pl-9 pr-4 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
              >
                <Icon name="X" size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Filtros rápidos */}
        <div className="px-5 pb-3 flex-shrink-0">
          <div className="flex gap-2 flex-wrap">
            {FILTROS?.map(f => (
              <button
                key={f?.id}
                onClick={() => setFiltroActivo(f?.id)}
                className={`
                  px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold transition-all
                  ${filtroActivo === f?.id
                    ? 'bg-[#D6EF4E] text-black'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70 border border-white/8'
                  }
                `}
              >
                {f?.label}
              </button>
            ))}
            <label className="flex items-center gap-1.5 cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={incluirCompletadas}
                onChange={e => setIncluirCompletadas(e?.target?.checked)}
                className="w-3.5 h-3.5 rounded border-white/20 bg-[#111] accent-[#D6EF4E]"
              />
              <span className="text-[11px] font-oranienbaum text-white/40">Completadas</span>
            </label>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Icon name="Loader2" size={24} className="text-[#D6EF4E]/50 animate-spin" />
              <p className="text-sm font-oranienbaum text-white/40">Cargando trabajos...</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-400/10 border border-red-400/20 text-red-400 text-sm font-oranienbaum">
              <Icon name="AlertCircle" size={15} />
              {error}
            </div>
          )}

          {!loading && !error && grouped?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                <Icon name="ClipboardX" size={22} className="text-white/25" />
              </div>
              <p className="text-sm font-oranienbaum text-white/40">
                {search ? `Sin resultados para "${search}"` : 'No hay trabajos disponibles con este filtro'}
              </p>
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="text-xs font-oranienbaum text-[#D6EF4E]/60 hover:text-[#D6EF4E] transition-colors"
                >
                  Limpiar búsqueda
                </button>
              )}
            </div>
          )}

          {!loading && !error && grouped?.map(({ fecha, trabajos }) => (
            <div key={fecha}>
              {/* Day header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-white/8" />
                <span className="text-[11px] font-oranienbaum font-bold text-white/40 tracking-widest uppercase">
                  {formatDayHeader(fecha === '__sin_fecha__' ? null : fecha)}
                </span>
                <div className="h-px flex-1 bg-white/8" />
              </div>

              {/* Trabajos del día */}
              <div className="space-y-3">
                {trabajos?.map(({ trabajo, piezas }) => {
                  // Get client color from trabajo config (if available via join)
                  const clienteColor = trabajo?.cliente_config?.color || trabajo?.color || null;
                  return (
                    <TrabajoCard
                      key={trabajo?.id}
                      trabajo={trabajo}
                      piezas={piezas}
                      clienteColor={clienteColor}
                      onSelectPieza={handleSelectPieza}
                      loadingPiezaId={loadingPiezaId}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/8 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/50 hover:text-white/70 hover:bg-white/8 transition-colors text-sm font-oranienbaum"
          >
            + Registro no planificado
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Exported Wrapper ─────────────────────────────────────────────────────────
// This is the component used in piece-registration/index.jsx

const PlanTrabajoSelector = ({ onContextLoaded, onClear, currentPiezaId }) => {
  const { user, userProfile } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContext, setSelectedContext] = useState(null);
  const [empleadoActualId, setEmpleadoActualId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Resolve current employee using user_id (NOT email)
  useEffect(() => {
    const resolveEmpleado = async () => {
      if (!user) return;
      try {
        setResolving(true);
        const role = userProfile?.role || 'employee';
        setIsAdmin(role === 'admin');
        if (role !== 'admin') {
          const empleado = await getCurrentEmpleado(user?.id);
          if (empleado?.id) setEmpleadoActualId(empleado?.id);
        }
      } catch (_) {}
      finally { setResolving(false); }
    };
    resolveEmpleado();
  }, [user, userProfile]);

  const handleSelectPieza = (ctx, pieza) => {
    setSelectedContext(ctx);
    onContextLoaded?.(ctx);
  };

  const handleClear = () => {
    setSelectedContext(null);
    onClear?.();
  };

  const estadoCfg = selectedContext
    ? ESTADO_CONFIG?.[selectedContext?.estadoPieza] || ESTADO_CONFIG?.PENDIENTE
    : null;

  return (
    <>
      <div className="rounded-xl border border-[#D6EF4E]/20 bg-[#D6EF4E]/4 overflow-hidden mb-5">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#D6EF4E]/10">
          <div className="flex items-center gap-2">
            <Icon name="ClipboardList" size={15} className="text-[#D6EF4E]/70" />
            <span className="text-sm font-bold font-oranienbaum text-[#D6EF4E]/90 uppercase tracking-wide">
              Trabajo Planificado
            </span>
          </div>
          {selectedContext && (
            <button
              onClick={handleClear}
              className="text-[11px] font-oranienbaum text-white/35 hover:text-white/60 flex items-center gap-1 transition-colors"
            >
              <Icon name="X" size={11} />
              Cambiar pieza
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {!selectedContext ? (
            /* Trigger button */
            <button
              onClick={() => setIsModalOpen(true)}
              disabled={resolving}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#111111] border border-white/10 hover:border-[#D6EF4E]/30 hover:bg-[#D6EF4E]/5 transition-all group active:scale-[0.99]"
            >
              <div className="flex items-center gap-2.5 text-white/50 group-hover:text-white/70 transition-colors">
                <Icon name="Search" size={15} />
                <span className="text-sm font-oranienbaum">Seleccionar trabajo planificado...</span>
              </div>
              <Icon name="ChevronRight" size={15} className="text-white/30 group-hover:text-[#D6EF4E]/60 transition-colors" />
            </button>
          ) : (
            /* Selected piece summary */
            <div className="space-y-3">
              <div
                className="p-3 rounded-xl bg-[#111111] border border-[#D6EF4E]/20"
                style={{ borderLeft: `3px solid ${selectedContext?.trabajo?.color || '#D6EF4E'}` }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold font-oranienbaum text-white text-sm truncate">
                      {selectedContext?.cliente?.nombre || '—'}
                    </p>
                    <p className="text-xs font-oranienbaum text-white/60 mt-0.5 truncate">
                      {selectedContext?.producto?.nombre || '—'}
                    </p>
                    <p className="text-[11px] font-oranienbaum text-white/40 mt-1">
                      Pieza {selectedContext?.numeroPieza} de {selectedContext?.totalPiezas}
                      {selectedContext?.empleado?.nombre && ` · ${selectedContext?.empleado?.nombre}`}
                    </p>
                  </div>
                  {estadoCfg && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-oranienbaum px-2 py-0.5 rounded-full flex-shrink-0 ${estadoCfg?.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${estadoCfg?.dot}`} />
                      {estadoCfg?.label}
                    </span>
                  )}
                </div>

                {/* Extra info */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedContext?.formatoCorte && (
                    <span className="text-[10px] bg-[#D6EF4E]/8 text-[#D6EF4E]/70 px-2 py-0.5 rounded border border-[#D6EF4E]/15 font-oranienbaum">
                      {selectedContext?.formatoCorte}
                    </span>
                  )}
                  {selectedContext?.quiereHuesos && (
                    <span className="text-[10px] bg-amber-400/10 text-amber-400/80 px-2 py-0.5 rounded border border-amber-400/20 font-oranienbaum">
                      🦴 Huesos
                    </span>
                  )}
                  {selectedContext?.guardarMerma && (
                    <span className="text-[10px] bg-green-400/10 text-green-400/80 px-2 py-0.5 rounded border border-green-400/20 font-oranienbaum">
                      ♻ Merma
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full py-2 rounded-lg bg-white/5 border border-white/8 text-white/40 hover:text-white/60 hover:bg-white/8 transition-colors text-xs font-oranienbaum flex items-center justify-center gap-1.5"
              >
                <Icon name="RefreshCw" size={11} />
                Cambiar pieza
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <PlanTrabajoSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectPieza={handleSelectPieza}
        empleadoActualId={empleadoActualId}
        isAdmin={isAdmin}
      />
    </>
  );
};

export default PlanTrabajoSelector;
